import { useMutation } from 'react-query'
import { ChangePasswordForm } from '../ChangePassword/ChangePassword'
import {
    getTheNightlyUpdatePasswordActionPath,
    encryptPass,
    getTheNightlyForgotPasswordActionPath,
} from '@news-mono/web-common'

interface ChangePasswordRequest extends ChangePasswordForm {
    userId: string
    email: string
    passEncryptionKey: string
}

export const useChangePassword = ({
    currentPassword,
    newPassword,
    userId,
    email,
    passEncryptionKey,
}: ChangePasswordRequest) => {
    const request = 'change-password-request'

    return useMutation<{ result: boolean; message: string }>(
        request,
        async () => {
            const encryptedCurrentPassword = await encryptPass(
                currentPassword,
                passEncryptionKey,
            )
            const encryptedNewPassword = await encryptPass(
                newPassword,
                passEncryptionKey,
            )
            const result = {
                result: false,
                message: 'Failed to update password, please try again.',
            }

            if (!encryptedCurrentPassword || !encryptedNewPassword) {
                return result
            }
            const response = await fetch(
                getTheNightlyUpdatePasswordActionPath,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userId,
                        current_password: encryptedCurrentPassword,
                        new_password: encryptedNewPassword,
                        email,
                    }),
                },
            )

            if (response.status === 401) {
                return {
                    result: false,
                    message: 'Incorrect password, please try again.',
                }
            }

            if (response.status === 201) {
                return {
                    result: true,
                    message: 'Password successfully updated.',
                }
            }

            return result
        },
    )
}

export const useForgotPassword = ({ email }: { email: string }) => {
    const request = 'forgot-password-request'

    return useMutation<{ result: boolean; message: string }>(
        request,
        async () => {
            const result = {
                result: false,
                message: 'Failed to send password reset email.',
            }

            const response = await fetch(
                getTheNightlyForgotPasswordActionPath,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email,
                    }),
                },
            )

            if (response.status === 200) {
                return {
                    result: true,
                    message:
                        "We've just sent you an email to reset your password.",
                }
            }
            return result
        },
    )
}

export function validatePassword({
    currentPassword,
    newPassword,
}: ChangePasswordForm): {
    valid: boolean
    message?: string
} {
    if (!currentPassword || !newPassword) {
        return {
            valid: false,
            message: 'Both your current password and new password are required',
        }
    }

    // Same password RegEx defined TN's Auth0 universal login template
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/

    if (!passwordRegex.test(newPassword)) {
        return {
            valid: false,
            message:
                'Password must contain at least 8 characters with a mix of upper case, lower case and digits.',
        }
    }

    return {
        valid: true,
    }
}
